import { locate, locateID } from "@xbs/lib-dom";
import { isDefined } from "../helpers";
export function dragManager(node, config) {
    if (config?.readonly) {
        return;
    }
    const api = config.api;
    let portal = null;
    let wrapper = null;
    let captured = null;
    let clone = null;
    let target = null;
    let line = null;
    let dragCoords;
    let draggableIds = [];
    let capturedId = null;
    let targetId = null;
    let dropPosition = null;
    let scrollTimer;
    let pointerDown = {};
    let handlers;
    let mode = null;
    let filtered = false;
    let hideCompleted = false;
    const touch = {
        duration: 500,
        timer: null,
        callback: null,
    };
    const touchStart = () => {
        touch.timer = setTimeout(touch.callback, touch.duration);
    };
    const touchEnd = () => {
        touch.timer && clearTimeout(touch.timer);
    };
    const checkScroll = (event) => {
        if (scrollTimer) {
            clearTimeout(scrollTimer);
        }
        if (wrapper) {
            const shift = 50;
            const contentRect = wrapper.getBoundingClientRect();
            const scrollState = {
                x: wrapper.scrollLeft,
                y: wrapper.scrollTop,
            };
            const { x, y } = getClientCoords(event);
            if (x > contentRect.width + contentRect.left - shift) {
                wrapper.scrollTo(scrollState.x + shift, scrollState.y);
            }
            if (x < contentRect.left + shift) {
                wrapper.scrollTo(scrollState.x - shift, scrollState.y);
            }
            if (y > contentRect.height + contentRect.top - shift) {
                wrapper.scrollTo(scrollState.x, scrollState.y + shift);
            }
            if (y < contentRect.top + shift) {
                wrapper.scrollTo(scrollState.x, scrollState.y - shift);
            }
            scrollTimer = setTimeout(() => {
                checkScroll(event);
            }, 100);
        }
    };
    const startDrag = (event) => {
        const { x, y } = getClientCoords(event);
        if (pointerDown?.x === x && pointerDown?.y === y) {
            return;
        }
        if (isDefined(capturedId)) {
            mode = event.altKey ? "copy" : "move";
            api.exec("start-drag", {
                start: capturedId,
                mode,
            });
            draggableIds = api.getState().draggableIds;
            setClone(event);
            document.body.classList.add("wx-ondrag");
            if ("touches" in event) {
                document.body.classList.add("wx-ondrag--touch");
            }
        }
    };
    const setTarget = (event) => {
        const { x, y } = getClientCoords(event);
        const underElement = document.elementFromPoint(x, y);
        const isSeparator = underElement && underElement.dataset.separator;
        if (!isSeparator) {
            target = underElement
                ? locate(underElement, "data-drag-list-id")
                : null;
            targetId = target ? locateID(target, "data-drag-list-id") : null;
            if (mode === "move" && api.getTask({ id: targetId })?.draggable) {
                targetId = null;
            }
        }
    };
    const moveDragItem = (event) => {
        dragCoords = getCurrentCoords(event);
        moveClone(dragCoords);
        setTarget(event);
        dropPosition = isDefined(targetId)
            ? getVerticalPositionType(event, target)
            : null;
        if (targetId) {
            setDroppedLine(dropPosition);
        }
        else {
            removeDroppedLine();
        }
        api.exec("drag", {
            start: capturedId,
            source: draggableIds,
            target: targetId,
            dropPosition,
        });
    };
    const setDroppedLine = (position) => {
        if (!portal.contains(line)) {
            line = document.createElement("div");
            line.classList.add("wx-todo_dropped-line");
            portal.appendChild(line);
        }
        const rect = target.getBoundingClientRect();
        if (position === "in") {
            line.style.opacity = "0.5";
            line.style.width = rect.width + "px";
            line.style.height = rect.height + "px";
            line.style.left = rect.x + "px";
            line.style.top = rect.y + "px";
            return;
        }
        const styles = getComputedStyle(target);
        const hasChildren = api.hasChildren({
            id: targetId,
            filtered,
            hideCompleted,
        });
        const paddingLeft = hasChildren ? 16 : 40;
        const level = parseInt(target.dataset.listLevel);
        const levelOffset = level === 1 ? (position === "top" ? -6.5 : 6.5) : 0;
        const heightOffset = position === "top" ? 0 : parseFloat(styles.height);
        const widthOffset = parseFloat(styles.paddingLeft) - (level === 1 ? paddingLeft : 0);
        const width = rect.width - widthOffset;
        const SEP_HEIGHT = 4;
        const SEP_OFFSET = 2;
        line.style.opacity = "1";
        line.style.width = width + "px";
        line.style.height = SEP_HEIGHT + "px";
        let parenOffset = 0;
        if (position === "bottom" && hasChildren) {
            const pRect = target.parentElement.getBoundingClientRect();
            if (level === 1) {
                parenOffset =
                    pRect.height -
                        heightOffset -
                        levelOffset -
                        SEP_HEIGHT -
                        SEP_OFFSET * 2;
            }
            else {
                parenOffset = pRect.height - heightOffset;
            }
        }
        line.style.top =
            rect.y +
                heightOffset +
                levelOffset +
                parenOffset -
                SEP_OFFSET +
                "px";
        line.style.left = rect.x + widthOffset + "px";
    };
    const removeDroppedLine = () => {
        if (portal.contains(line)) {
            portal.removeChild(line);
        }
    };
    const moveClone = ({ x, y }) => {
        clone.style.left = x + "px";
        clone.style.top = y + "px";
    };
    const setClone = (event) => {
        const hasChildren = api.hasChildren({
            id: targetId,
            filtered,
            hideCompleted,
        });
        const paddingLeft = hasChildren ? 16 : 40;
        if (draggableIds.length > 1) {
            portal.style.setProperty("--wx-todo-dragged-tasks-count", JSON.stringify(draggableIds.length < 99 ? `${draggableIds.length}` : "+99"));
        }
        dragCoords = getCurrentCoords(event);
        portal.appendChild(clone);
        clone.classList.add("wx-todo__dragged-task");
        clone.style.paddingLeft = paddingLeft + "px";
        clone.style.left = dragCoords.x + "px";
        clone.style.top = dragCoords.y + "px";
    };
    const removeClone = () => {
        if (portal.contains(clone)) {
            portal.removeChild(clone);
            portal.style.removeProperty("--wx-todo-dragged-tasks-count");
        }
    };
    const downHandler = (event) => {
        const { stateEditableItem, menuState, stateDueDate } = api.getState();
        const isCtrlMode = event.ctrlKey || event.metaKey;
        const isShiftMode = event.shiftKey;
        const isOpenEditor = isDefined(stateEditableItem);
        if (isCtrlMode ||
            isShiftMode ||
            isOpenEditor ||
            stateDueDate?.open ||
            menuState ||
            (event.touches &&
                event.touches.length > 1) ||
            ("button" in event && event.button !== 0)) {
            return;
        }
        portal = document.querySelector(".wx-todo");
        wrapper = document.querySelector(`[data-todo-wrapper-id="${config.id}"]`);
        captured = locate(event.target, "data-drag-list-id");
        capturedId = locateID(event.target, "data-drag-list-id");
        handlers =
            "touches" in event
                ? { up: "touchend", move: "touchmove" }
                : { up: "pointerup", move: "mousemove" };
        if (captured && wrapper) {
            filtered = !!api.getState().filter?.match;
            hideCompleted = api.getState().taskShape?.completed?.taskHide;
            const rect = captured.getBoundingClientRect();
            const styles = getComputedStyle(captured);
            const hasChildren = api.hasChildren({
                id: capturedId,
                filtered,
                hideCompleted,
            });
            const paddingLeft = hasChildren ? 16 : 40;
            const leftOffset = parseInt(styles.paddingLeft) - paddingLeft;
            clone = captured.cloneNode(true);
            clone.style.width = rect.width - leftOffset + "px";
            clone.style.height = rect.height + "px";
            const { x, y } = (pointerDown = getClientCoords(event));
            pointerDown.shiftX = x - rect.x - leftOffset;
            pointerDown.shiftY = y - rect.y;
            if ("touches" in event) {
                touch.callback = () => {
                    document.addEventListener(handlers.move, moveHandler);
                };
                touchStart();
            }
            else {
                document.addEventListener(handlers.move, moveHandler);
            }
        }
        document.addEventListener(handlers.up, upHandler);
    };
    const moveHandler = (event) => {
        if (!pointerDown) {
            return;
        }
        if (!("touches" in event)) {
            event.preventDefault();
        }
        event.stopPropagation();
        checkScroll(event);
        if (!draggableIds?.length) {
            startDrag(event);
        }
        else {
            moveDragItem(event);
        }
    };
    const upHandler = () => {
        document.removeEventListener(handlers.move, moveHandler);
        document.removeEventListener(handlers.up, upHandler);
        touchEnd();
        removeClone();
        removeDroppedLine();
        if (scrollTimer) {
            clearTimeout(scrollTimer);
        }
        if (draggableIds.length) {
            draggableIds = [];
            api.exec("end-drag", {
                target: targetId,
                dropPosition,
                mode,
            });
        }
        document.body.classList.remove("wx-ondrag");
        document.body.classList.remove("wx-ondrag--touch");
        portal =
            captured =
                clone =
                    line =
                        target =
                            wrapper =
                                dragCoords =
                                    targetId =
                                        capturedId =
                                            pointerDown =
                                                null;
    };
    const getCurrentCoords = (event) => {
        const { x, y } = getClientCoords(event);
        return {
            x: x - pointerDown.shiftX,
            y: y - pointerDown.shiftY,
        };
    };
    node.addEventListener("mousedown", downHandler);
    node.addEventListener("touchstart", downHandler);
    return {
        destroy() {
            node.removeEventListener("mousedown", downHandler);
            node.removeEventListener("touchstart", downHandler);
        },
    };
}
function getVerticalPositionType(event, target) {
    if (!target)
        return null;
    const { top, height } = target.getBoundingClientRect();
    const offset = (getClientCoords(event).y - top) / height;
    if (offset <= 0.25)
        return "top";
    if (offset >= 0.75)
        return "bottom";
    return "in";
}
function getClientCoords(event) {
    const coords = { x: 0, y: 0 };
    if ("touches" in event) {
        coords.x = event.touches[0].clientX;
        coords.y = event.touches[0].clientY;
    }
    else {
        coords.x = event.clientX;
        coords.y = event.clientY;
    }
    return coords;
}
//# sourceMappingURL=dnd.js.map